import { BasePagination, HStack, IconButton, Tooltip, useCustomToast, useDisclosure } from '@elkaso-app/web-design';
import { ICrmDedDetailBusiness, useGetCrmDedDetailsApi } from 'apis/crm-ded-details';
import { BaseSortButton, BaseTable, BaseTableContainer } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableHeader,
} from 'components/base-table/components';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import React, { useEffect, useState } from 'react';
import { FiGrid } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

// Components
import { BusinessNameFilterButton } from '../components/filters/business-name-filter';
import { EditDetailModal } from '../components/modals/edit-detail-modal';
import { ViewDetailModal } from '../components/modals/view-detail-modal';
import { StatsCards } from '../components/stats-cards';
import { ActionsCell } from '../components/table-cells/actions-cell';
import { BusinessCell } from '../components/table-cells/business-cell';
import { LicensesCell } from '../components/table-cells/licenses-cell';

const CrmDedDetailsListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();
  const toast = useCustomToast();
  const history = useHistory();

  // Modals state
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const [selectedDetailId, setSelectedDetailId] = useState<number | null>(null);
  const [selectedBusinessName, setSelectedBusinessName] = useState<string>('');

  // API hooks
  const { isLoading, isFetching, data, error } = useGetCrmDedDetailsApi({
    onError: (err) => {
      console.error('🚨 DED Details API Error in component:', err);
      toast({
        title: 'Failed to Load Data',
        description: 'Unable to load DED details. Please check your connection and try again.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  // Debug logging
  useEffect(() => {
    console.log('🔍 DED Details Component State:', {
      isLoading,
      isFetching,
      data,
      error,
      hasData: !!data,
      dataLength: data?.data?.length,
    });
  }, [isLoading, isFetching, data, error]);

  const { pageCount, total, data: dedDetailsData } = data ?? {};

  // Handlers
  const handleViewDetail = (detailId: number, businessName: string) => {
    setSelectedDetailId(detailId);
    setSelectedBusinessName(businessName);
    onViewOpen();
  };

  const handleEditDetail = (detailId: number, businessName: string) => {
    setSelectedDetailId(detailId);
    setSelectedBusinessName(businessName);
    onEditOpen();
  };

  // Set page title
  useEffect(() => {
    dispatch(changeTitle('CRM DED Details Management'));
  }, [dispatch]);

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <TableHeader
            name='Business Information'
            sortButtonComponent={<BaseSortButton sortBy='businessName' />}
            filterButtonComponent={<BusinessNameFilterButton />}
          />
        ),
        accessor: 'business',
        width: '35%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={BusinessCell} {...value} />,
      },
      {
        Header: <TableHeader name='Licenses' sortButtonComponent={<BaseSortButton sortBy='totalLicenses' />} />,
        accessor: 'licenses',
        width: '30%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={LicensesCell} {...value} />,
      },
      {
        Header: <TableHeader name='Primary Contact' />,
        accessor: 'contact',
        width: '20%',
        Cell: ({ value }: CellProps<any, any>) => (
          <div style={{ height: '100px', display: 'flex', alignItems: 'center', padding: '12px 0' }}>
            <div>
              {value?.mobile && (
                <div style={{ fontSize: '14px', color: '#4A5568', marginBottom: '4px' }}>📱 {value.mobile}</div>
              )}
              {value?.telephone && (
                <div style={{ fontSize: '14px', color: '#4A5568', marginBottom: '4px' }}>📞 {value.telephone}</div>
              )}
              {value?.email && (
                <div style={{ fontSize: '14px', color: '#4A5568', marginBottom: '4px' }}>✉️ {value.email}</div>
              )}
              {(!value || (!value.mobile && !value.telephone && !value.email)) && (
                <div style={{ fontSize: '14px', color: '#A0AEC0', fontStyle: 'italic' }}>No contact info</div>
              )}
            </div>
          </div>
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        width: '15%',
        Cell: ({ value }: CellProps<any, any>) => <CustomTableCell as={ActionsCell} {...value} />,
      },
    ],
    []
  );

  const tableData = React.useMemo(
    () =>
      dedDetailsData?.map((item: ICrmDedDetailBusiness) => ({
        id: item.businessId || item.businessName, // Use business ID or name as unique identifier for the row
        business: {
          businessName: item.businessName,
          englishName: item.englishName,
          primaryContact: item.primaryContact,
          primaryLocation: item.primaryLocation,
          summary: item.summary,
        },
        licenses: {
          licenses: item.licenses,
          businessName: item.businessName,
        },
        contact: item.primaryContact,
        actions: {
          detailId: item.licenses[0]?.id || 0, // Use first license ID for detail operations
          businessName: item.businessName,
          onView: (detailId: number) => handleViewDetail(detailId, item.businessName),
          onEdit: (detailId: number) => handleEditDetail(detailId, item.businessName),
        },
      })) || [],
    [dedDetailsData]
  );

  return (
    <>
      {/* Statistics Cards */}
      <StatsCards />

      <BaseTableContainer isLoading={isLoading}>
        <BaseTableContainer.Header>
          <BaseTableContainer.Header.Left>
            <BaseTableTitle title='DED Details Management' />
            <BaseTableSpinner isFetching={isFetching} />
          </BaseTableContainer.Header.Left>

          <BaseTableContainer.Header.Right>
            <HStack spacing={3}>
              <Tooltip label='Switch to Enhanced License View'>
                <IconButton
                  aria-label='Enhanced License View'
                  icon={<FiGrid />}
                  size='sm'
                  colorScheme='blue'
                  variant='outline'
                  onClick={() => history.push('/app/crm-ded-details/enhanced')}
                />
              </Tooltip>
              <ClearFilterButton />
            </HStack>
          </BaseTableContainer.Header.Right>
        </BaseTableContainer.Header>

        <BaseTableContainer.TableWrapper>
          <BaseTable columns={columns} data={tableData} />
        </BaseTableContainer.TableWrapper>

        <BaseTableContainer.Footer>
          <BaseTableContainer.Footer.Left>
            {total && (
              <div style={{ fontSize: '14px', color: '#4A5568' }}>
                Showing {dedDetailsData?.length || 0} of {total} businesses
              </div>
            )}
          </BaseTableContainer.Footer.Left>
          <BaseTableContainer.Footer.Right>
            <BasePagination>
              <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
              <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
            </BasePagination>
          </BaseTableContainer.Footer.Right>
        </BaseTableContainer.Footer>
      </BaseTableContainer>

      {/* Modals */}
      {selectedDetailId && (
        <>
          <ViewDetailModal
            isOpen={isViewOpen}
            onClose={onViewClose}
            detailId={selectedDetailId}
            businessName={selectedBusinessName}
          />
          <EditDetailModal
            isOpen={isEditOpen}
            onClose={onEditClose}
            detailId={selectedDetailId}
            businessName={selectedBusinessName}
          />
        </>
      )}
    </>
  );
};

export default CrmDedDetailsListScreen;
